import React, { useEffect, useLayoutEffect, useRef } from 'react';

import { DefaultTooltipContent, Tooltip, type TooltipProps, type TooltipValueType } from 'recharts';

import { tooltipPosition } from './placement';

let pointer = { x: 0, y: 0 };
const listeners = new Set<() => void>();
let tracking = 0;
function trackPointer(event: PointerEvent) {
  pointer = { x: event.clientX, y: event.clientY };
  listeners.forEach((listener) => listener());
}

function FloatingContent({ active, children }: { active: boolean; children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!active || !ref.current) return;
    const place = () => {
      const node = ref.current;
      if (!node) return;
      const position = tooltipPosition(pointer, node.getBoundingClientRect(), {
        width: window.innerWidth,
        height: window.innerHeight,
      });
      node.style.left = `${position.x}px`;
      node.style.top = `${position.y}px`;
      node.style.visibility = 'visible';
    };
    const hide = () => {
      if (ref.current) ref.current.style.visibility = 'hidden';
    };
    place();
    listeners.add(place);
    window.addEventListener('resize', hide);
    window.addEventListener('scroll', hide, true);
    return () => {
      listeners.delete(place);
      window.removeEventListener('resize', hide);
      window.removeEventListener('scroll', hide, true);
    };
  }, [active, children]);
  if (!active) return null;
  return (
    <div
      ref={ref}
      role="tooltip"
      style={{
        position: 'fixed',
        pointerEvents: 'none',
        zIndex: 1500,
        maxWidth: 'min(440px, calc(100vw - 16px))',
        width: 'max-content',
        whiteSpace: 'normal',
        overflowWrap: 'anywhere',
        boxSizing: 'border-box',
        background: '#FFFFFF',
        color: '#0F0C08',
        border: '1px solid #0F464F',
        borderRadius: 4,
        boxShadow: '0 4px 18px rgba(15, 12, 8, 0.14)',
      }}
    >
      {children}
    </div>
  );
}

/** Report-only chart overlays escape scroll containers and stay inside the viewport. */
export default function ReportChartTooltip(props: TooltipProps<TooltipValueType, string | number>) {
  useEffect(() => {
    if (tracking++ === 0) window.addEventListener('pointermove', trackPointer, { passive: true });
    return () => {
      if (--tracking === 0) window.removeEventListener('pointermove', trackPointer);
    };
  }, []);
  const { content, ...rest } = props;
  return (
    <Tooltip
      {...rest}
      portal={typeof document === 'undefined' ? undefined : document.body}
      wrapperStyle={{ pointerEvents: 'none', position: 'fixed', left: 0, top: 0, transform: 'none', zIndex: 1500 }}
      isAnimationActive={false}
      content={(tooltipProps) => (
        <FloatingContent active={tooltipProps.active}>
          {typeof content === 'function' ? (
            content(tooltipProps)
          ) : React.isValidElement(content) ? (
            React.cloneElement(content, tooltipProps)
          ) : (
            <DefaultTooltipContent
              {...tooltipProps}
              contentStyle={{ ...props.contentStyle, whiteSpace: 'normal', maxWidth: '100%', boxSizing: 'border-box' }}
              itemStyle={{ ...props.itemStyle, whiteSpace: 'normal', overflowWrap: 'anywhere' }}
            />
          )}
        </FloatingContent>
      )}
    />
  );
}
